import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FolderView from './components/FolderView';
import UploadSection from './components/UploadSection';
import AuthModal from '../../components/auth/AuthModal';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

export default function ResourcesPage() {
  const { isAdmin, user, adminUser } = useAuth();
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [selectedSubfolder, setSelectedSubfolder] = useState<string>('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [showDeleteFolder, setShowDeleteFolder] = useState<string | null>(null);
  const [deletingFolder, setDeletingFolder] = useState(false);
  const [folders, setFolders] = useState<Array<{id: string, name: string, icon: string, color: string, protected: boolean}>>([]);
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingFolder, setPendingFolder] = useState<string | null>(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [unlockedFolders, setUnlockedFolders] = useState<Set<string>>(new Set());
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Derive admin status directly from user — always reactive
  // REMOVED local isAdminUser — use adminUser from useAuth directly

  const staticFolderConfig: Record<string, {icon: string, color: string, protected: boolean}> = {
    'Grade 7 Cambridge': { icon: 'ri-book-2-line', color: 'from-blue-500 to-blue-600', protected: true },
    'Grade 8 Cambridge': { icon: 'ri-book-2-line', color: 'from-indigo-500 to-indigo-600', protected: true },
    'Grade 9 Cambridge': { icon: 'ri-book-2-line', color: 'from-purple-500 to-purple-600', protected: true },
    'Grade 10 Cambridge': { icon: 'ri-book-2-line', color: 'from-pink-500 to-pink-600', protected: true },
    'Grade 11 Cambridge': { icon: 'ri-book-2-line', color: 'from-red-500 to-red-600', protected: true },
    'Grade 11 cambridge': { icon: 'ri-book-2-line', color: 'from-red-500 to-red-600', protected: true },
    'Grade 12 Cambridge': { icon: 'ri-book-2-line', color: 'from-orange-500 to-orange-600', protected: true },
    'Grade 12 cambridge': { icon: 'ri-book-2-line', color: 'from-orange-500 to-orange-600', protected: true },
    'A Level Cambridge': { icon: 'ri-graduation-cap-line', color: 'from-amber-500 to-amber-600', protected: false },
    'Grade 9 Edexcel': { icon: 'ri-book-line', color: 'from-yellow-500 to-yellow-600', protected: true },
    'Grade 10 Edexcel': { icon: 'ri-book-line', color: 'from-lime-500 to-lime-600', protected: true },
    'A Levels': { icon: 'ri-graduation-cap-line', color: 'from-green-500 to-green-600', protected: true },
    'IB-AI-SL': { icon: 'ri-calculator-line', color: 'from-emerald-500 to-emerald-600', protected: true },
    'IB-AI-HL': { icon: 'ri-calculator-line', color: 'from-teal-500 to-teal-600', protected: true },
    'IB-AA-SL': { icon: 'ri-calculator-line', color: 'from-cyan-500 to-cyan-600', protected: true },
    'IB-AA-HL': { icon: 'ri-calculator-line', color: 'from-sky-500 to-sky-600', protected: true },
    'IB Mathematics': { icon: 'ri-calculator-line', color: 'from-blue-500 to-blue-600', protected: true },
    'AP Calculus': { icon: 'ri-function-line', color: 'from-indigo-500 to-indigo-600', protected: false },
    'SAT': { icon: 'ri-file-list-3-line', color: 'from-purple-500 to-purple-600', protected: true },
    'Mathematics Topics': { icon: 'ri-numbers-line', color: 'from-pink-500 to-pink-600', protected: true },
    'Maths animation': { icon: 'ri-movie-line', color: 'from-red-500 to-red-600', protected: true },
    'Maths Animation': { icon: 'ri-movie-line', color: 'from-red-500 to-red-600', protected: true },
    'Physics Animation': { icon: 'ri-movie-2-line', color: 'from-violet-500 to-violet-600', protected: true },
    'physics animation': { icon: 'ri-movie-2-line', color: 'from-violet-500 to-violet-600', protected: true },
    'Cambridge Past Papers and Solutions': { icon: 'ri-file-list-3-line', color: 'from-emerald-500 to-emerald-600', protected: true },
    'cambridge past papers and solutions': { icon: 'ri-file-list-3-line', color: 'from-emerald-500 to-emerald-600', protected: true },
    'Cambridge Past Papers and solutions': { icon: 'ri-file-list-3-line', color: 'from-emerald-500 to-emerald-600', protected: true },
    'Grade 9 ICT': { icon: 'ri-computer-line', color: 'from-cyan-500 to-cyan-600', protected: true },
    'General Resources': { icon: 'ri-folder-line', color: 'from-gray-500 to-gray-600', protected: false },
  };

  const fetchFolders = async () => {
    try {
      setLoadingFolders(true);
      
      // Get all unique folders from the database
      const { data, error } = await supabase
        .from('resources')
        .select('folder')
        .not('folder', 'is', null);

      if (error) throw error;

      // Get unique folder names
      const uniqueFolders = [...new Set(data?.map(r => r.folder) || [])];
      
      // Map to folder objects with icons, colors, and protection status
      const folderList = uniqueFolders.map(folderName => {
        const config = staticFolderConfig[folderName] || {
          icon: 'ri-folder-line',
          color: 'from-violet-500 to-violet-600',
          protected: false
        };
        
        return {
          id: folderName,
          name: folderName,
          icon: config.icon,
          color: config.color,
          protected: config.protected
        };
      });

      setFolders(folderList);
    } catch (error) {
      console.error('Error fetching folders:', error);
    } finally {
      setLoadingFolders(false);
    }
  };

  useEffect(() => {
    fetchFolders();
  }, [refreshKey]);

  const handleFolderClick = (folderId: string) => {
    // Admin can access all folders without password
    if (isAdmin()) {
      setSelectedFolder(folderId);
      setSelectedSubfolder('');
      return;
    }

    // Check if folder is already unlocked
    if (unlockedFolders.has(folderId)) {
      setSelectedFolder(folderId);
      setSelectedSubfolder('');
      return;
    }

    // Get folder config
    const folderConfig = staticFolderConfig[folderId];
    const isProtected = folderConfig?.protected || false;

    // Check if folder is protected
    if (isProtected) {
      // Show password modal
      setPendingFolder(folderId);
      setShowPasswordModal(true);
      setPasswordInput('');
      setPasswordError('');
      return;
    }

    // No password needed
    setSelectedFolder(folderId);
    setSelectedSubfolder('');
  };

  const handleBack = () => {
    setSelectedFolder(null);
    setSelectedSubfolder('');
    // Refresh folders when coming back
    fetchFolders();
  };

  const handleUploadComplete = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleSubfolderChange = (subfolder: string) => {
    setSelectedSubfolder(subfolder);
  };

  const handleCreateFolder = async () => {
    if (!isAdmin()) {
      alert('Only administrators can create folders');
      return;
    }

    if (!newFolderName.trim()) {
      alert('Please enter a folder name');
      return;
    }

    if (folders.some(f => f.id === newFolderName.trim())) {
      alert('A folder with this name already exists');
      return;
    }

    try {
      setCreatingFolder(true);
      
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('You must be logged in as admin');
        return;
      }

      // Create a folder entry in the database via edge function
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-subfolder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            folder: newFolderName.trim(),
            subfolder: '',
            parentPath: null
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to create folder');
      }

      setShowCreateFolder(false);
      setNewFolderName('');
      setRefreshKey(prev => prev + 1);
      
      alert('Folder created successfully! You can now upload files to it.');
      
    } catch (error: any) {
      console.error('Create folder error:', error);
      alert(error.message || 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (!isAdmin()) {
      alert('Only administrators can delete folders');
      return;
    }

    try {
      setDeletingFolder(true);
      
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('You must be logged in as admin');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/delete-folder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ folderId: folderId })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete folder');
      }

      setShowDeleteFolder(null);
      setRefreshKey(prev => prev + 1);
      
      alert(`Folder deleted successfully! ${result.filesDeleted || 0} files removed.`);
      
    } catch (error: any) {
      console.error('Delete folder error:', error);
      alert(error.message || 'Failed to delete folder');
    } finally {
      setDeletingFolder(false);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!pendingFolder || !passwordInput.trim()) return;

    try {
      setVerifyingPassword(true);
      setPasswordError('');

      // Verify password via backend - no login required
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/verify-folder-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            folderName: pendingFolder,
            password: passwordInput
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 404) {
          setPasswordError('This folder does not have a password set. Please contact the administrator.');
        } else {
          throw new Error(result.error || 'Failed to verify password');
        }
        return;
      }

      if (result.valid) {
        // Password correct - unlock folder
        setUnlockedFolders(prev => new Set([...prev, pendingFolder]));
        setSelectedFolder(pendingFolder);
        setSelectedSubfolder('');
        setShowPasswordModal(false);
        setPendingFolder(null);
        setPasswordInput('');
        setPasswordError('');
      } else {
        // Password incorrect
        setPasswordError('Incorrect password. Please try again.');
        setPasswordInput('');
      }
    } catch (error: any) {
      console.error('Password verification error:', error);
      setPasswordError(error.message || 'Failed to verify password. Please try again.');
    } finally {
      setVerifyingPassword(false);
    }
  };

  const handlePasswordModalClose = () => {
    setShowPasswordModal(false);
    setPendingFolder(null);
    setPasswordInput('');
    setPasswordError('');
  };

  if (selectedFolder) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FolderView 
            key={refreshKey}
            folderId={selectedFolder} 
            onBack={handleBack}
            onRefresh={handleUploadComplete}
            onSubfolderChange={handleSubfolderChange}
          />
          
          {/* Upload Section - always visible */}
          <div className="mt-8">
            <UploadSection 
              onUploadComplete={handleUploadComplete}
              selectedFolder={selectedFolder}
              selectedSubfolder={selectedSubfolder}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Learning Resources</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Access comprehensive educational materials organized by grade level and curriculum
          </p>
        </div>

        {/* Create Folder Button */}
        {isAdmin() && (
          <div className="mb-8 flex justify-end gap-3">
            <Link
              to="/admin/passwords"
              className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 shadow-md"
            >
              <i className="ri-shield-keyhole-line text-xl"></i>
              <span className="font-medium">Manage Passwords</span>
            </Link>
            <button
              onClick={() => setShowCreateFolder(true)}
              className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 shadow-md"
            >
              <i className="ri-folder-add-line text-xl"></i>
              <span className="font-medium">Create New Folder</span>
            </button>
          </div>
        )}

        {/* Loading State */}
        {loadingFolders ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-4"></i>
              <p className="text-gray-600">Loading folders...</p>
            </div>
          </div>
        ) : folders.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl shadow-md">
            <i className="ri-folder-open-line text-5xl text-gray-400 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Folders Yet</h3>
            <p className="text-gray-600 mb-6">Create your first folder to get started</p>
            {isAdmin() && (
              <button
                onClick={() => setShowCreateFolder(true)}
                className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className="ri-folder-add-line text-xl"></i>
                <span className="font-medium">Create New Folder</span>
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Folders Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {folders.map((folder) => (
                <div key={folder.id} className="relative group">
                  <div
                    onClick={() => handleFolderClick(folder.id)}
                    className="w-full bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 border border-gray-200 hover:border-teal-300 cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-14 h-14 flex items-center justify-center bg-gradient-to-br ${folder.color} rounded-xl shadow-lg group-hover:scale-110 transition-transform`}>
                        <i className={`${folder.icon} text-white text-2xl`}></i>
                      </div>
                      <div className="flex items-center gap-2">
                        {!isAdmin() && folder.protected && !unlockedFolders.has(folder.id) && (
                          <i className="ri-lock-line text-xl text-gray-400"></i>
                        )}
                        {isAdmin() && folder.protected && (
                          <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium">Admin</span>
                        )}
                        <i className="ri-arrow-right-line text-2xl text-gray-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all"></i>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                      {folder.name}
                    </h3>
                    
                    <p className="text-sm text-gray-600">
                      {!isAdmin() && folder.protected && !unlockedFolders.has(folder.id)
                        ? 'Password required'
                        : 'Click to explore resources'}
                    </p>
                  </div>

                  {/* Delete Button (Admin Only) - Positioned outside the clickable area */}
                  {isAdmin() && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteFolder(folder.id);
                      }}
                      className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 shadow-lg cursor-pointer z-10"
                      title="Delete folder"
                    >
                      <i className="ri-delete-bin-line text-base"></i>
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Info Section */}
            <div className="mt-16 bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8 border border-teal-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-xl flex-shrink-0">
                  <i className="ri-information-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">How to Use Resources</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-600 mt-1"></i>
                      <span>Click on any folder to view and access learning materials</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-600 mt-1"></i>
                      <span>Resources are organized by grade level and curriculum type</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-600 mt-1"></i>
                      <span>Download materials for offline study and reference</span>
                    </li>
                    {!isAdmin() && (
                      <li className="flex items-start gap-2">
                        <i className="ri-lock-line text-teal-600 mt-1"></i>
                        <span>Some folders require a password to access</span>
                      </li>
                    )}
                    {isAdmin() && (
                      <>
                        <li className="flex items-start gap-2">
                          <i className="ri-check-line text-teal-600 mt-1"></i>
                          <span>Create new folders to organize different subjects or topics</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-check-line text-teal-600 mt-1"></i>
                          <span>Delete folders by hovering over them and clicking the delete button</span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Password Modal */}
      {showPasswordModal && pendingFolder && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handlePasswordModalClose();
            }
          }}
        >
          <div className="bg-white rounded-xl max-w-md w-full p-6 relative">
            <button
              onClick={handlePasswordModalClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
              disabled={verifyingPassword}
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lock-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Password Required</h3>
              <p className="text-sm text-gray-600">
                Enter the password to access <strong>{pendingFolder}</strong>
              </p>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              handlePasswordSubmit();
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={passwordInput}
                  onChange={(e) => {
                    setPasswordInput(e.target.value);
                    setPasswordError('');
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Enter password"
                  autoFocus
                  disabled={verifyingPassword}
                />
                {passwordError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {passwordError}
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handlePasswordModalClose}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  disabled={verifyingPassword}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
                  disabled={!passwordInput.trim() || verifyingPassword}
                >
                  {verifyingPassword ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-lock-unlock-line"></i>
                      <span>Unlock</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCreateFolder(false);
              setNewFolderName('');
            }
          }}
        >
          <div className="bg-white rounded-xl max-w-md w-full p-6 relative">
            <button
              onClick={() => {
                setShowCreateFolder(false);
                setNewFolderName('');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-folder-add-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Create New Folder</h3>
              <p className="text-sm text-gray-600">
                Add a new folder to organize your resources
              </p>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              handleCreateFolder();
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Folder Name
                </label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Grade 13 Cambridge"
                  autoFocus
                  disabled={creatingFolder}
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateFolder(false);
                    setNewFolderName('');
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  disabled={creatingFolder}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
                  disabled={creatingFolder || !newFolderName.trim()}
                >
                  {creatingFolder ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-add-line"></i>
                      <span>Create Folder</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Folder Confirmation Modal */}
      {showDeleteFolder && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mr-4">
                <i className="ri-alert-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Folder</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the folder "<strong>{showDeleteFolder}</strong>" and all its contents? This action cannot be undone.
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteFolder(null)}
                disabled={deletingFolder}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteFolder(showDeleteFolder)}
                disabled={deletingFolder}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {deletingFolder ? 'Deleting...' : 'Delete Folder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}